import type { ISuccessApiResponse } from '@wyz/types';
import { UseMutationOptions, restrictApi, useMutation } from '@wyz/utils';
import { useApi, useConfig } from '~/common/context';
import { IQuoteEditPayload, IQuoteErrorResponse } from '~/purchase/types';
import { PurchaseQueryKeys } from '~/purchase/api';
import { isNil, omitBy } from 'lodash-es';

type IQuoteVariables = {
  customerId: string;
  reference: string;
};

type Options = {
  resendMail?: UseMutationOptions<
    ISuccessApiResponse,
    IQuoteErrorResponse,
    IQuoteVariables
  >;
  duplicate?: UseMutationOptions<
    ISuccessApiResponse,
    IQuoteErrorResponse,
    IQuoteVariables
  >;
  edit?: UseMutationOptions<
    ISuccessApiResponse,
    IQuoteErrorResponse,
    { body: IQuoteEditPayload } & IQuoteVariables
  >;
};

export const useManageQuotes = <
  TRequestedKeys extends keyof Options,
  TRequestedOptions extends Options = Pick<Options, TRequestedKeys>,
>(
  options: TRequestedOptions,
) => {
  const { tenantCode } = useConfig();
  const { callCoreApi } = useApi();

  const resendMailMutation = useMutation({
    mutationFn: (variables) =>
      callCoreApi<ISuccessApiResponse>(
        `/offers/api/tenants/${tenantCode}/customers/${variables.customerId}/quotes/${variables.reference}/resend-email`,
        { method: 'POST' },
      ),
    retry: false,
    keysToInvalidateOnSuccess: (variable) => [
      PurchaseQueryKeys.quotes.paginate(variable.customerId),
    ],
    ...options.resendMail,
  });

  const duplicateMutation = useMutation({
    mutationFn: (variables) =>
      callCoreApi<ISuccessApiResponse>(
        `/offers/api/tenants/${tenantCode}/customers/${variables.customerId}/quotes/${variables.reference}/duplicate`,
        { method: 'POST' },
      ),
    retry: false,
    keysToInvalidateOnSuccess: (variable) => [
      PurchaseQueryKeys.quotes.paginate(variable.customerId),
    ],
    ...options.duplicate,
  });

  const editMutation = useMutation({
    mutationFn: ({ customerId, reference, body }) =>
      callCoreApi<ISuccessApiResponse>(
        `/offers/api/tenants/${tenantCode}/customers/${customerId}/quotes/${reference}`,
        { method: 'PUT', body: JSON.stringify(omitBy(body, isNil)) },
      ),
    retry: false,
    ...options.edit,
  });

  return restrictApi(options, {
    resendMail: resendMailMutation,
    duplicate: duplicateMutation,
    edit: editMutation,
  });
};
